import { useMutation, useQuery } from '@apollo/react-hooks';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import AccountDetails from '../../components/dashboard/AccountDetails';
import Nav from '../../components/dashboard/Nav';
import SellerSettings from '../../components/dashboard/SellerSettings';
import { Box, Column, Row } from '../../components/Flex';
import { HeadingOne } from '../../components/Text';
import GraphQLErrorText from '../../components/GraphQLErrorText';
import Loading from '../../components/Loading';
import useAuthOrRedirect from '../../hooks/useAuthOrRedirect';
import {
  CHECK_STRIPE_SELLER_APPROVAL,
  ONBOARD_STRIPE_SELLER,
  REMOVE_STRIPE_SELLER_ACCOUNT,
  SIGN_UP_FOR_AFFILIATE,
} from '../../lib/graphql/mutations';
import { ACCOUNT_SETTINGS } from '../../lib/graphql/queries';
import { COLORS } from '../../theme';
import { Media } from '../../theme/media';
import { analytics } from '../../lib/analytics';

const AvatarContainer = styled.div`
  text-align: center;
  width: 100%;
  height: 12rem;
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 5px 36px #919db529;
  border: 2px solid #e1eaf3;
  border-radius: 15px;
  opacity: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  @media only screen and (max-width: 1100px) {
    border: 1px solid #e5e8f0;
    box-shadow: 0 0;
  }
`;

const AvatarTitle = styled.p`
  color: ${COLORS.BLACK};
  font: normal normal 600 21px/31px Poppins;
  letter-spacing: 0px;
  text-align: center;
  margin-bottom: 0;
`;

const AvatarSubtitle = styled.p`
  text-align: center;
  font: normal normal medium 15px/23px Poppins;
  letter-spacing: 0px;
  color: ${COLORS.GRAY};
  margin: 0;
`;

const AvatarImageContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  border-radius: 100%;
  background-color: #e5e5e5;
  height: 100px;
  width: 100px;
`;
const AvatarImage = styled(Image)`
  border-radius: 100%;
  text-align: center;
`;

const Avatar = ({ firstName, lastName, username, imageUrl }) => (
  <AvatarContainer>
    <AvatarImageContainer>
      {imageUrl ? (
        <AvatarImage height={100} width={100} src={imageUrl} alt="" />
      ) : null}
    </AvatarImageContainer>
    <AvatarTitle>
      {firstName} {lastName}
    </AvatarTitle>
    <AvatarSubtitle>{username}</AvatarSubtitle>
  </AvatarContainer>
);

const LargeContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin: auto;
`;

const LargePanels = styled.div`
  display: flex;
  & > *:first-child {
    margin-right: 1.5rem;
  }
`;

const SmallContainer = styled.div`
  display: flex;
  flex-direction: column;
  & > * {
    margin-bottom: 1.5rem;
  }
`;

const Container = styled(Row)`
  margin-bottom: 8.75rem;
  margin-right: auto;
  margin-left: auto;
  width: 100%;
  max-width: 100%;
  @media only screen and (max-width: 800px) {
    flex-direction: column;
    width: 90%;
  }
  @media only screen and (max-width: 500px) {
    width: 100%;
  }
`;

const Title = styled.p`
  font: normal normal 700 25px/38px Poppins;
  letter-spacing: 0px;
  color: #111723;
  opacity: 1;
  width: 100%;
  text-align: left;
`;

const Account = () => {
  const { loading, data, error, refetch } = useQuery(ACCOUNT_SETTINGS, {
    fetchPolicy: 'no-cache',
    ssr: false,
  });
  const [onboardStripeSeller, onboardStripeSellerResult] = useMutation(
    ONBOARD_STRIPE_SELLER,
  );
  const [signUpAffiliate, signUpAffiliateResult] = useMutation(
    SIGN_UP_FOR_AFFILIATE,
  );
  const [country, setCountry] = useState({ label: 'USA', value: 'US' });
  const [removeStripeSellerAccount] = useMutation(REMOVE_STRIPE_SELLER_ACCOUNT);
  const [checkStripeSellerApproval, checkStripeSellerApprovalResult] =
    useMutation(CHECK_STRIPE_SELLER_APPROVAL);
  useEffect(() => {
    analytics.page('Rental Settings');
  }, []);
  if (!useAuthOrRedirect()) return null;

  const title = <HeadingOne className="lg:mb-5">Account</HeadingOne>;

  if (error) {
    return <GraphQLErrorText error={error} />;
  }

  if (loading)
    return (
      <>
        <Container>
          <Column>
            <Media lessThan="lg">
              <SmallContainer>
                {title}
                <h3 className="h-[25vh] w-full bg-gray-200 rounded-md dark:bg-gray-700"></h3>

                <h3
                  className="h-48 bg-gray-200 rounded-md dark:bg-gray-700"
                  style={{ width: '100%' }}
                ></h3>

                <h3
                  className="h-28 bg-gray-200 rounded-md dark:bg-gray-700"
                  style={{ width: '100%' }}
                ></h3>
              </SmallContainer>
            </Media>
            <Media greaterThanOrEqual="lg">
              <LargeContainer>
                {title}
                <LargePanels className="flex w-full animate-pulse">
                  <div className="w-1/4">
                    <h3
                      className="h-[25vh] bg-gray-200 rounded-xl dark:bg-gray-700"
                      style={{ width: '100%' }}
                    ></h3>
                    <Box height={8} />
                    <h3
                      className="h-[50vh] bg-gray-200 rounded-xl dark:bg-gray-700"
                      style={{ width: '100%' }}
                    ></h3>
                  </div>
                  <div className="w-3/4">
                    {' '}
                    <h3
                      className="h-[75vh] bg-gray-200 rounded-xl dark:bg-gray-700"
                      style={{ width: '100%' }}
                    ></h3>
                  </div>
                </LargePanels>
              </LargeContainer>
            </Media>
          </Column>
        </Container>
      </>
    );

  const accountDetails = (
    <AccountDetails
      {...{
        props: data.me,
        onRefetch: () => {
          console.log('refetching data');
          refetch();
        },
      }}
    />
  );
  const avatar = <Avatar {...data.me} />;

  const onboard = () => {
    onboardStripeSeller({
      variables: {
        countryCode: country.value,
      },
    })
      .then((res) => {
        analytics.track('User Seller Onboarding Started', {
          country: country.value,
        });
        window.location.assign(res.data.onboardStripeSeller);
      })
      .catch(() => {});
  };

  const deleteAccount = () => {
    if (confirm('Are you sure you want to restart the onboarding process?')) {
      removeStripeSellerAccount()
        .then(() => {
          analytics.track('User Seller Account Deleted');
          window.location.reload();
        })
        .catch(() => {});
    }
  };

  if (
    !checkStripeSellerApprovalResult.called &&
    data.me.stripeAccountId &&
    !data.me.isStripeSellerAccountApproved
  ) {
    checkStripeSellerApproval()
      .then((res) => {
        if (res.data.checkStripeSellerApproval) {
          analytics.track('User Seller Stripe Approved');
          window.location.assign(res.data.checkStripeSellerApproval);
        }
      })
      .catch(() => {});
  }

  const sellerSettings = (
    <SellerSettings
      loading={
        checkStripeSellerApprovalResult.loading ||
        onboardStripeSellerResult.loading ||
        signUpAffiliateResult.loading
      }
      affiliateUrl={
        data.affiliatePortalLink && data.affiliatePortalLink.affiliateUrl
      }
      signUpForAffiliate={() => {
        signUpAffiliate()
          .then(() => {
            if (typeof window !== 'undefined') {
              window.location.reload();
            } else {
              analytics.track('User Affiliate Sign Up');
              toast.success('Successfully signed up. Please refresh the page.');
            }
          })
          .catch((err) => toast.error(err.message));
      }}
      onboard={onboard}
      deleteAccount={deleteAccount}
      country={country}
      setCountry={setCountry}
      {...data.me}
    />
  );

  return (
    <Container>
      <Column>
        <Media lessThan="lg">
          <SmallContainer>
            {title}
            {avatar}
            {accountDetails}
            {sellerSettings}
          </SmallContainer>
        </Media>
        <Media greaterThanOrEqual="lg">
          <LargeContainer>
            {title}
            <LargePanels className="flex w-full">
              <div className="w-1/4">
                {avatar}
                <Box height={8} />
                {sellerSettings}
              </div>
              <div className="w-3/4">{accountDetails}</div>
            </LargePanels>
          </LargeContainer>
        </Media>
      </Column>
    </Container>
  );
};

export async function getStaticProps() {
  return { props: {} };
}

export default Account;
